import * as React from 'react';
// eslint-disable-next-line no-restricted-imports
import { TextField as MuiTextField } from '@material-ui/core';
import { TextFieldProps as MuiTextFieldProps } from '@material-ui/core/TextField';
import { makeStyles } from '@material-ui/core/styles';

export const useInputClasses = makeStyles((theme) => ({
  input: {
    padding: (props: TextFieldProps) =>
      props.multiline ? '0' : props.label ? '20px 12px 7px' : '13px 12px 14px',
  },
  root: {
    border: `1px solid ${theme.colors.borderDefault}`,
    backgroundColor: theme.colors.bgDefault,
    padding: (props: TextFieldProps) =>
      props.multiline ? (props.label ? '20px 12px 7px' : '13px 12px 14px') : '0',
    paddingLeft: (props: TextFieldProps) =>
      props.multiline ? '12px' : props.InputProps?.startAdornment ? 12 : 0,
    paddingRight: (props: TextFieldProps) =>
      props.multiline ? '12px' : props.InputProps?.endAdornment ? 12 : 0,
    borderRadius: 4,
    '&:hover': {
      backgroundColor: theme.colors.bgDefault,
      border: `1px solid ${theme.colors.bgDarker}`,
    },
    '&.Mui-error': {
      border: `1px solid ${theme.colors.borderDangerBase}`,
    },
    '&$focused': {
      backgroundColor: theme.colors.bgDefault,
      boxShadow: 'none',
      borderColor: theme.colors.borderPrimaryBase,
    },
    '& .MuiInputAdornment-positionStart': {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      marginTop: (props: any) =>
        props.label ? `${theme.typography.pxToRem(16)} !important` : `0 !important`,
    },
  },
  adornedStart: {
    '& .MuiFilledInput-inputAdornedStart': {
      paddingLeft: 0,
    },
  },
  adornedEnd: {
    '& .MuiFilledInput-inputAdornedEnd': {
      paddingRight: 0,
    },
  },
  focused: {},
}));

export const useInputLabelClasses = makeStyles((theme) => ({
  filled: {
    color: theme.colors.fgDefault,
    transform: 'translate(12px, 16px) scale(1)',
    '&.MuiInputLabel-shrink': {
      color: theme.colors.fgSubtle,
      transform: 'translate(12px, 8px) scale(0.75) !important',
    },
    '&.Mui-focused': {
      color: theme.colors.fgPrimaryBase,
    },
  },
  asterisk: {
    display: 'none',
  },
}));

export const useSelectInputClasses = makeStyles((theme) => ({
  root: {
    '& .MuiOutlinedInput-adornedStart': {
      paddingLeft: 0,
    },
  },
  select: {
    '& .MuiSelect-root.MuiSelect-select': {
      width: theme.typography.pxToRem(50),
      paddingTop: theme.typography.pxToRem(4),
      paddingBottom: theme.typography.pxToRem(4),
      paddingLeft: 0,
    },
    '&:before': {
      borderWidth: 0,
    },
    '&:hover:not(.Mui-disabled):before': {
      borderWidth: 0,
    },
    '&:after': {
      borderWidth: 0,
    },
  },
}));

export const useHelperTextStyles = makeStyles((theme) => ({
  root: {
    marginLeft: 0,
  },
}));

export const useHelperTextWarnStyles = makeStyles((theme) => ({
  root: {
    color: theme.colors.fgWarningBase,
    marginLeft: 0,
  },
}));

export const useHelperTextValidStyles = makeStyles((theme) => ({
  root: {
    color: theme.colors.fgSuccessBase,
    marginLeft: 0,
  },
}));

export interface TextFieldProps
  extends Pick<MuiTextFieldProps, Exclude<keyof MuiTextFieldProps, 'variant'>> {
  warn?: boolean;
  valid?: boolean;
}
/**
 * Use `TextField` for rendering a text field or text area with autosizing
 *
 * ```js
 * import { TextField } from 'ui';
 * ```
 */
const TextField: React.FunctionComponent<TextFieldProps> = ({
  warn,
  valid,
  select,
  InputProps,
  InputLabelProps,
  error,
  label,
  multiline,
  ...rest
}) => {
  const warnClasses = useHelperTextWarnStyles();
  const successClasses = useHelperTextValidStyles();
  const inputClassesLocal = useInputClasses({ label, multiline, InputProps });
  const inputClasses = InputProps?.classes
    ? { ...inputClassesLocal, ...InputProps.classes }
    : inputClassesLocal;
  const inputLabelClasses = useInputLabelClasses();
  let helperTextClasses = useHelperTextStyles();

  if (warn) {
    helperTextClasses = warnClasses;
  }

  if (valid) {
    helperTextClasses = successClasses;
  }

  return (
    <MuiTextField
      multiline={multiline}
      error={error}
      FormHelperTextProps={{
        classes: helperTextClasses,
        'aria-live': 'polite',
      }}
      InputProps={{
        ...InputProps,
        classes: inputClasses,
        disableUnderline: true,
      }}
      InputLabelProps={{
        ...InputLabelProps,
        classes: inputLabelClasses,
      }}
      variant="filled"
      label={label}
      {...rest}
    />
  );
};

export default TextField;

// https://github.com/mui-org/material-ui/issues/13898 see for workaround for start adornment
